import { useState, useEffect } from "react"
import { X } from "lucide-react"
import { Button } from "@/components/ui/button"
import { ChatIcon } from "@/components/icons"
import "../styles/fonts.css"

// Import font files directly
import StolzlRegular from '/fonts/stolzl_regular.otf'
import StolzlMedium from '/fonts/stolzl_medium.otf'

// Add font loading check
const loadFonts = async () => {
  try {
    // Load Stolzl fonts
    const stolzlRegular = new FontFace('Stolzl', `url(${StolzlRegular})`, {
      weight: 'normal',
      style: 'normal',
    });
    const stolzlMedium = new FontFace('Stolzl', `url(${StolzlMedium})`, {
      weight: '500',
      style: 'normal',
    });

    await Promise.all([stolzlRegular.load(), stolzlMedium.load()]);
    document.fonts.add(stolzlRegular);
    document.fonts.add(stolzlMedium);
    console.log('Stolzl fonts loaded successfully');
  } catch (error) {
    console.error('Failed to load fonts:', error);
  }
};

export function MyStylus() {
  const [isOpen, setIsOpen] = useState(false)
  const [showFirstPopup, setShowFirstPopup] = useState(false)
  const [showSecondPopup, setShowSecondPopup] = useState(false)
  const [showGenerateButton, setShowGenerateButton] = useState(false)
  const [showOpenWidgetGreeting, setShowOpenWidgetGreeting] = useState(false)
  const [showOpenWidgetWelcome, setShowOpenWidgetWelcome] = useState(false)
  const [showOpenWidgetButton, setShowOpenWidgetButton] = useState(false)
  const [showOpenWidgetExtraButtons, setShowOpenWidgetExtraButtons] = useState(false)
  const [showExtraButtons, setShowExtraButtons] = useState(false)
  const [pageH1Text, setPageH1Text] = useState("")
  const [popupsClosed, setPopupsClosed] = useState(false)

  // Function to get H1 text from the page
  const getPageH1Text = () => {
    const h1Elements = document.getElementsByTagName('h1');
    if (h1Elements.length > 0) {
      return h1Elements[0].textContent || "";
    }
    return "";
  };

  useEffect(() => {
    loadFonts();
    // Get the H1 text when component mounts
    setPageH1Text(getPageH1Text());
  }, []);

  const toggleChat = () => setIsOpen(!isOpen)
  const closeAllMessages = () => {
    setShowFirstPopup(false)
    setShowSecondPopup(false)
    setShowGenerateButton(false)
    setShowExtraButtons(false)
    setPopupsClosed(true)
  }

  useEffect(() => {
    if (popupsClosed) return; // Don't show popups if they were manually closed

    if (isOpen) {
      // For open widget: reset states first
      setShowOpenWidgetGreeting(false)
      setShowOpenWidgetWelcome(false)
      setShowOpenWidgetButton(false)
      setShowOpenWidgetExtraButtons(false)
      
      // Show greeting after a short delay
      const greetingTimer = setTimeout(() => {
        setShowOpenWidgetGreeting(true)
      }, 500)
      
      // Show welcome message 1.5 seconds after greeting
      const welcomeTimer = setTimeout(() => {
        setShowOpenWidgetWelcome(true)
      }, 2000)
      
      // Show button 1 second after welcome message
      const buttonTimer = setTimeout(() => {
        setShowOpenWidgetButton(true)
      }, 3000)
      
      // Show extra buttons 1 second after first button
      const extraButtonsTimer = setTimeout(() => {
        setShowOpenWidgetExtraButtons(true)
      }, 4000)
      
      return () => {
        clearTimeout(greetingTimer)
        clearTimeout(welcomeTimer)
        clearTimeout(buttonTimer)
        clearTimeout(extraButtonsTimer)
      }
    } else {
      // For collapsed widget: reset states first
      setShowFirstPopup(false)
      setShowSecondPopup(false)
      setShowGenerateButton(false)
      setShowExtraButtons(false)
      
      // First popup appears after 1 second
      const firstTimer = setTimeout(() => {
        setShowFirstPopup(true)
      }, 1000)
      
      // Second popup appears 2 seconds after first
      const secondTimer = setTimeout(() => {
        setShowSecondPopup(true)
      }, 3000)
      
      // Button appears 1.5 seconds after second popup
      const buttonTimer = setTimeout(() => {
        setShowGenerateButton(true)
      }, 4500)
      
      // Extra buttons appear 1 second after first button
      const extraButtonsTimer = setTimeout(() => {
        setShowExtraButtons(true)
      }, 5500)
      
      return () => {
        clearTimeout(firstTimer)
        clearTimeout(secondTimer)
        clearTimeout(buttonTimer)
        clearTimeout(extraButtonsTimer)
      }
    }
  }, [isOpen])

  // Create greeting message with H1 text
  const greetingMessage = pageH1Text 
    ? `Ищешь информацию по теме "${pageH1Text}"?` 
    : "Нужна помощь с учебными задачами? Я помогу!";

  return (
    <div className="fixed bottom-6 right-6 z-[99999999999] my-stylus-container msd-widget-stylus">
      {isOpen ? (
        <div className="fixed inset-0 sm:relative sm:inset-auto bg-white sm:rounded-3xl shadow-lg w-full sm:w-[400px] h-screen sm:h-[500px] flex flex-col overflow-hidden">
          {/* Header */}
          <div className="widget-padding flex items-center justify-between border-b shrink-0">
            <div className="flex items-center">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-[#7D2AEB] rounded-full flex items-center justify-center">
                  <ChatIcon className="w-6 h-6 text-white" />
                </div>
                <div className="flex flex-col">
                  <span className="text-lg font-medium text-[#101720]">Study24 AI</span>
                  <span className="text-xs text-gray-500">Нейросети для учебы, работы и развлечений</span>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => {
                closeAllMessages();
                toggleChat();
              }}>
                <X className="h-5 w-5 text-gray-600" />
              </Button>
            </div>
          </div>

          {/* Message Content */}
          <div className="flex-1 overflow-y-auto p-3 w-full">
            <div className="flex flex-col space-y-3">
              {/* First greeting message */}
              {showOpenWidgetGreeting && (
                <div className="inline-block max-w-[45%] animate-in fade-in slide-in-from-bottom-3 duration-300 ease-out">
                  <div className="bg-white rounded-[16px] p-5 shadow-[0px_10px_25px_0px_rgba(125,42,235,0.11)] transform-gpu transition-all">
                    <p className="text-gray-900 text-[14px] leading-[20px] whitespace-nowrap">
                      Привет! 👋
                    </p>
                  </div>
                </div>
              )}
              
              {/* Platform description message */}
              {showOpenWidgetWelcome && (
                <div className="inline-block max-w-[85%] animate-in fade-in slide-in-from-bottom-3 duration-300 ease-out">
                  <div className="bg-white rounded-[16px] p-5 shadow-[0px_10px_25px_0px_rgba(125,42,235,0.11)] transform-gpu transition-all">
                    <div className="space-y-4">
                      <p className="text-gray-900 text-[14px] leading-[20px]">
                        Попробуй проект, объединяющий все нейросети для учебы, работы и саморазвития. Генерация текстов, картинок, видео, презентаций и многое другое. Переходи по ссылке и почувствуй всю мощь ИИ
                      </p>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Generate button */}
              {showOpenWidgetButton && (
                <div className="inline-block max-w-[85%] animate-in fade-in slide-in-from-bottom-3 duration-300 ease-out">
                  <Button
                    asChild
                    className="w-full h-auto bg-[#7D2AEB] hover:bg-[#6B22D1] hover:text-white text-white font-[500] text-[14px] leading-[20px] rounded-[8px] font-['Stolzl'] p-3 transform-gpu transition-all"
                    onClick={() => {
                      window.ym?.(100299336, 'reachGoal', 'academic_work');
                    }}
                  >
                    <a 
                      href="https://eduforms.org?rid=d93c523a3285c65e"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Рефераты
                    </a>
                  </Button>
                </div>
              )}
              
              {/* Extra buttons */}
              {showOpenWidgetExtraButtons && (
                <div className="inline-block max-w-[85%] animate-in fade-in slide-in-from-bottom-3 duration-300 ease-out">
                  <div className="flex space-x-2">
                    <Button
                      asChild
                      className="flex-1 h-auto bg-[#7D2AEB] hover:bg-[#6B22D1] hover:text-white text-white font-[500] text-[14px] leading-[20px] rounded-[8px] font-['Stolzl'] p-3 transform-gpu transition-all"
                      onClick={() => {
                        window.ym?.(100299336, 'reachGoal', 'presentations');
                      }}
                    >
                      <a 
                        href="https://eduforms.org/?rid=de825b1c6f634216&ulp=https%3A%2F%2Fstudy24.ai%2Fchat%2Fgenerate_presentation_plus_bot"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Презентации
                      </a>
                    </Button>
                    <Button
                      asChild
                      className="flex-1 h-auto bg-[#7D2AEB] hover:bg-[#6B22D1] hover:text-white text-white font-[500] text-[14px] leading-[20px] rounded-[8px] font-['Stolzl'] p-3 transform-gpu transition-all"
                      onClick={() => {
                        window.ym?.(100299336, 'reachGoal', 'problem_solving');
                      }}
                    >
                      <a 
                        href="https://eduforms.org/?rid=175ceef97ba2ac92&ulp=https%3A%2F%2Fstudy24.ai%2Fchat%2Fa24_reshatel_toe_bot"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Решение задач
                      </a>
                    </Button>
                  </div>
                  <Button
                    asChild
                    className="w-full h-auto bg-[#f75db8] hover:bg-[#e54ca7] hover:text-white text-white font-[500] text-[14px] leading-[20px] rounded-[8px] font-['Stolzl'] p-3 transform-gpu transition-all mt-2"
                    onClick={() => {
                      window.ym?.(100299336, 'reachGoal', 'avtor24_order');
                    }}
                  >
                    <a 
                      href="https://express.avtor24.ru/?ref=483e3848f6057f95"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Разместить заказ в Автор24
                    </a>
                  </Button>
                </div>
              )}
            </div>
          </div>
          {/* Footer */}
          <div className="p-2 text-center border-t text-[14px] leading-[20px] text-gray-400 font-['Stolzl']">
            by Edugram
          </div>
        </div>
      ) : (
        <div className="relative">
          <div className="absolute bottom-[68px] right-0 space-y-3 bg-white/15 backdrop-blur-sm rounded-2xl p-3">
            {showFirstPopup && (
              <div className="animate-in fade-in slide-in-from-bottom-3 duration-300 ease-out scale-95 initial-hidden animate-show">
                <div className={`relative bg-white rounded-[16px] shadow-[0px_10px_25px_0px_rgba(125,42,235,0.11)] p-6 w-80 transform-gpu transition-all font-['Stolzl'] group`}>
                  <button 
                    onClick={closeAllMessages}
                    className="absolute -top-2 -right-2 bg-[#6B7280] hover:bg-[#4B5563] text-white w-6 h-6 rounded-full flex items-center justify-center initial-hidden group-hover:opacity-100 transition-opacity"
                  >
                    <X className="h-3 w-3" />
                  </button>
                  <div className="space-y-3">
                    <div className="space-y-4">
                      <p className="text-sm font-[400] text-gray-900 text-[0.938rem]">
                        {greetingMessage}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}
            
            {showSecondPopup && (
              <div className="animate-in fade-in slide-in-from-bottom-3 duration-300 ease-out scale-95 initial-hidden animate-show">
                <div className={`relative bg-white rounded-[16px] shadow-[0px_10px_25px_0px_rgba(125,42,235,0.11)] p-6 w-80 transform-gpu transition-all font-['Stolzl'] group`}>
                  <div className="space-y-3">
                    <div className="space-y-4">
                      <p className="text-sm font-[400] text-gray-900 text-[0.938rem]">Попробуй проект, объединяющий все нейросети для учебы, работы и саморазвития. Генерация текстов, картинок, видео, презентаций и многое другое.</p>
                    </div>
                  </div>
                </div>
              </div>
            )}
            
            {showGenerateButton && (
              <div className="animate-in fade-in slide-in-from-bottom-3 duration-300 ease-out scale-95 initial-hidden animate-show">
                <Button
                  asChild
                  className="w-80 h-auto bg-[#7D2AEB] hover:bg-[#6B22D1] hover:text-white text-white font-[500] text-[14px] leading-[20px] rounded-[8px] font-['Stolzl'] p-3 transform-gpu transition-all"
                  onClick={() => {
                    window.ym?.(100299336, 'reachGoal', 'academic_work');
                  }}
                >
                  <a 
                    href="https://eduforms.org?rid=d93c523a3285c65e"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Рефераты
                  </a>
                </Button>
              </div>
            )}
            
            {showExtraButtons && (
              <div className="animate-in fade-in slide-in-from-bottom-3 duration-300 ease-out scale-95 initial-hidden animate-show">
                <div className="flex space-x-2 w-80">
                  <Button
                    asChild
                    className="flex-1 h-auto bg-[#7D2AEB] hover:bg-[#6B22D1] hover:text-white text-white font-[500] text-[14px] leading-[20px] rounded-[8px] font-['Stolzl'] p-3 transform-gpu transition-all"
                    onClick={() => {
                      window.ym?.(100299336, 'reachGoal', 'presentations');
                    }}
                  >
                    <a 
                      href="https://eduforms.org/?rid=de825b1c6f634216&ulp=https%3A%2F%2Fstudy24.ai%2Fchat%2Fgenerate_presentation_plus_bot"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Презентации
                    </a>
                  </Button>
                  <Button
                    asChild
                    className="flex-1 h-auto bg-[#7D2AEB] hover:bg-[#6B22D1] hover:text-white text-white font-[500] text-[14px] leading-[20px] rounded-[8px] font-['Stolzl'] p-3 transform-gpu transition-all"
                    onClick={() => {
                      window.ym?.(100299336, 'reachGoal', 'problem_solving');
                    }}
                  >
                    <a 
                      href="https://eduforms.org/?rid=175ceef97ba2ac92&ulp=https%3A%2F%2Fstudy24.ai%2Fchat%2Fa24_reshatel_toe_bot"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Решение задач
                    </a>
                  </Button>
                </div>
                <Button
                  asChild
                  className="w-80 h-auto bg-[#f75db8] hover:bg-[#e54ca7] hover:text-white text-white font-[500] text-[14px] leading-[20px] rounded-[8px] font-['Stolzl'] p-3 transform-gpu transition-all mt-2"
                  onClick={() => {
                    window.ym?.(100299336, 'reachGoal', 'avtor24_order');
                  }}
                >
                  <a 
                    href="https://express.avtor24.ru/?ref=483e3848f6057f95"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Разместить заказ в Автор24
                  </a>
                </Button>
              </div>
            )}
          </div>
          <Button
            onClick={toggleChat}
            className="rounded-full w-12 h-12 bg-[#7D2AEB] hover:bg-[#6B22D1] p-0 flex items-center justify-center [&_svg]:!w-auto [&_svg]:!h-auto"
          >
            <ChatIcon className="w-6 h-6 text-white" />
          </Button>
        </div>
      )}
    </div>
  )
}